import { makeClassName } from '@siteless/util';
import { OneUserFavoritesResource } from '../resources/UserFavorites/One';
import useCopy from '../util/useCopy';
import { HeartOutlineIcon } from './HeartOutlineIcon';
import { tmdbConfig } from '../config/constants';
import ObjectiveStateResource from '../resources/ObjectiveState';
import withSuspense from './withSuspense';
import { useState } from 'react';
import { FavoritesModal } from './FavoritesModal';
import ArrowRightIcon from './ArrowRightIcon';
import { CurrentUserMustResource } from '../resources/Auth/CurrentUserMust';
import { Sentry } from '../config/sentry';

export let FavoritesBar = withSuspense(
  ({
    userId,
    userHandle,
    className,
  }: {
    userId: string;
    userHandle: string;
    className?: string;
  }) => {
    let copy = useCopy();
    let currentUser = CurrentUserMustResource.use();
    let objectiveState = ObjectiveStateResource.use();
    let isCurrentUser = currentUser.id === userId;

    let favorites = OneUserFavoritesResource.use(userId);
    let isEmpty = !favorites || !favorites.length;

    let [isEditing, setIsEditing] = useState(false);

    if (isEmpty && isCurrentUser)
      return (
        <div
          className={makeClassName('bg-neutral-100 rounded-lg p-4', className)}
        >
          <div className="text-neutral-400 text-center text-sm">
            {copy.Tap} <HeartOutlineIcon className="inline-block mx-1" />{' '}
            {copy._whenFillingOutYourBallotToAddFavorites}
          </div>
        </div>
      );

    if (favorites === null || isEmpty) return null;

    return (
      <>
        <FavoritesModal
          userId={userId}
          userHandle={userHandle}
          isOpen={isEditing}
          onClose={() => {
            setIsEditing(false);
          }}
        />
        <div
          className={makeClassName('bg-neutral-100 rounded-lg py-2', className)}
        >
          <div className="text-sm font-medium mb-2 px-4 flex justify-between">
            <span>{copy.Favorites}</span>
            {isCurrentUser && (
              <ArrowRightIcon
                className="text-rose-bright h-4 w-4"
                onClick={() => {
                  setIsEditing(true);
                }}
              />
            )}
          </div>
          <div className="flex overflow-auto px-4">
            {favorites.map(({ type, key }) => {
              let imageUrl;
              let title;

              if (type === 'person') {
                // @ts-expect-error key is key of people
                let person = objectiveState.people[key];
                if (!person) {
                  Sentry.captureException(new Error(`Favorite person not found: ${key}`));
                  return null;
                }
                title = person.name;
                if (person.tmdb_data)
                  imageUrl = [
                    tmdbConfig.base_url,
                    tmdbConfig.poster_sizes.w185,
                    person.tmdb_data.tmdb_profile_path,
                  ].join('');
              } else {
                // @ts-expect-error key is key of films
                let film = objectiveState.films[key];
                if (!film) {
                  Sentry.captureException(new Error(`Favorite film not found: ${key}`));
                  return null;
                }
                title = film.title;

                if (film.tmdb_data)
                  imageUrl = [
                    tmdbConfig.base_url,
                    tmdbConfig.poster_sizes.w185,
                    film.tmdb_data.tmdb_poster_path,
                  ].join('');
              }

              return (
                <div
                  key={key}
                  className="flex-none h-12 w-12 overflow-hidden rounded-full mr-2 last:mr-0 border-2 border-rose-bright"
                >
                  {imageUrl ? (
                    <img src={imageUrl} alt={title} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[7px] text-center bg-neutral-0">
                      {title}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  },
);
